import { ConditionParams } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { createSetDisableLoadingAction, createUpdateMasterFrameDataSourceConditionAction } from "@multiweb/reactCore/datasource";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { IListener } from "Global/Base/Events";
import { AnyConstructor, Mixin } from "Global/Base/types";
import { ConditionOperator, StringUtils } from "Global/Base/Utils/StringUtils";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IActualObjectChangedEventArgs } from "UI/UI/UI/XGControls/Frames";
import { FrameSharedMethods } from "UI/UI/UI/XGControls/Frames/FrameSharedMethods";
import { IMasterableBase, IMasterableFrame } from "../../UI/UI/XGControls/Frames/FrameInterfaces";
import { IReactFrameConnector } from "../types";

const { dispatchAction } = bridge;

/**
 * Mixin, který přidává masterFrame na ConnectorFrame.
 *
 * @param Base Base třída rozšiřující IConnectorFrame.
 */
export const HasMasterFrame = <T extends AnyConstructor<IReactFrameConnector & IMasterableBase>>(Base: T) =>
    class HasMasterFrame extends Base {
        /**
         * Obsahuje master současného framu.
         */
        public _masterFrame: IMasterableFrame = undefined;

        public _masterObject: OWObject;
        /**
         * Název nadřízeného framu, musí se shodovat s frameName jiného framu nebo bude prázdný.
         */
        public override masterFrameName: string | null = undefined;
        /**
         * Název vazby na nadřízený objekt.
         */
        public override masterLinkToAttr: string | null = undefined;
        /**
         * Název vazby z nadřízeného objektu, pokud není vyplněn použije se původně vybraný objekt.
         */
        public override masterAttr: string | null = undefined;
        /**
         * Názvy aplikačních atributů, v nichž jsou hodnoty k argumentům podmínky.
         */
        public conditionParamsAttrs: string[] | null = undefined;

        public override get masterFrame(): IMasterableFrame {
            return this._masterFrame;
        }

        public override set masterFrame(masterFrame: IMasterableFrame) {
            if (this.element == null) // disposed
                return;

            this.masterFrame?.actualObjectChanged.remove(this._masterObjectChangeListener);

            this._masterFrame = masterFrame;

            this.masterFrame?.actualObjectChanged.add(this._masterObjectChangeListener);
        }

        public override get masterObject(): OWObject {
            if (this.masterFrame) {
                return this.masterFrame.actualObject;
            }
            if (this.isMainFrame && this.ownerForm?.masterObject) {
                return this.ownerForm.masterObject;
            }
            return this._masterObject;
        }

        public override set masterObject(v: OWObject) {
            this._masterObject = v;
        }

        /** Určuje, zda je na masterovi vybrán nějaký záznam */
        public get isMasterRecordSelected(): boolean {
            return this.masterFrame?.actualObject && !this.masterFrame.actualObject.isNew;
        }

        public _masterObjectChangeListener: IListener = async (
            _sender: IMasterableFrame,
            _e: IActualObjectChangedEventArgs,
            refreshOnChange: boolean = true,
        ) => {
            if (!this.isInitialized)
                return;

            let condition = "";
            if (this.isMainFrame) {
                // přidá se podmínka vazby na nadřízený záznam
                if (this.ownerForm.masterLinkToAttr && this.ownerForm.masterObject && !this.ownerForm.masterObject.isNew)
                    condition = this.ownerForm.masterLinkToAttr + "=" + this.ownerForm.masterObject.id; // v masterObject uz je koncovy objekt po uplatneni pripadneho MasterAttr

                // přidá se podmínka čtení pro formulář, pokud je nastavená (má přednost)
                if (this.ownerForm.formCondition)
                    condition = StringUtils.combineConditions(ConditionOperator.and, condition, this.ownerForm.formCondition);

                this._dispatchMasterFrameConditionChange(FrameSharedMethods.appendReadRightCondition(condition, this), true, refreshOnChange);
            } else {
                if (this.masterFrame && this.masterFrame.actualObject == null) {
                    this._dispatchDisableLoadingChange(true);
                    return;
                }

                if (this.isMasterRecordSelected) {
                    const masterId = await this._getMasterId();
                    const conditionParams = await FrameSharedMethods.createReaderConditionParams(this);
                    this._dispatchMasterFrameConditionChange(`${this.masterLinkToAttr}=${masterId}`, true, refreshOnChange, conditionParams);
                }
            }
        };

        constructor(...args: any[]) {
            super(...args);

            this.masterFrameName = this.element.getAttribute("data-masterframe");
            this.masterAttr = this.element.getAttribute("data-masterattr");
            this.useMasterContext = DataConverter.toBoolean(this.element.getAttribute("data-UseMasterContext"));
            this.masterLinkToAttr = this.element.getAttribute("data-masterlinktoattr") || "ID";

            const conditionParamsString = this.element.getAttribute("data-conditionparamsstring");
            if (conditionParamsString) {
                this.conditionParamsAttrs = conditionParamsString.split(";");
            }
        }

        public async _getMasterId(): Promise<any> {
            if (this.masterAttr && this.masterAttr !== "") {
                return await this.masterFrame.actualObject.getValue(this.masterAttr + ".ID");
            }
            return this.masterFrame.actualObject.id;
        }

        public _getConditionParamsAttributes(): string[] {
            if (this.masterAttr && this.masterAttr !== "") {
                return this.conditionParamsAttrs.map(attr => this.masterAttr + "." + attr);
            }
            return this.conditionParamsAttrs;
        }

        public async _getConditionParamsValues(): Promise<ConditionParams> {
            const conditionParamsAttrs = this._getConditionParamsAttributes();
            const attrValues = await this.masterFrame.actualObject.getValues(conditionParamsAttrs);
            return attrValues.map(param => param.toString());
        }

        public _dispatchMasterFrameConditionChange(condition: string, isMasterRecordSelected: boolean, refreshOnChange: boolean, conditionParams?: ConditionParams): void {
            dispatchAction(createUpdateMasterFrameDataSourceConditionAction(this.element.id, condition, conditionParams, isMasterRecordSelected, refreshOnChange));
        }

        public _dispatchDisableLoadingChange(value: boolean): void {
            dispatchAction(createSetDisableLoadingAction(this.element.id, value));
        }

        public override async initializeVisual(): Promise<void> {
            await super.initializeVisual();

            this._masterObjectChangeListener(undefined, undefined, false);
        }

        public override dispose(): void {
            this.masterFrame?.actualObjectChanged.remove(this._masterObjectChangeListener);
            super.dispose();
        }
    };

export type HasMasterFrame = Mixin<typeof HasMasterFrame>;
